import { Component, ReactNode, ErrorInfo } from 'react'
import { ErrorTrackingService, ErrorMetadata } from '../services/errorTracking'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error) => void
  componentName?: string
  metadata?: Omit<ErrorMetadata, 'componentName'>
}

interface State {
  hasError: boolean
  error?: Error
  errorId?: number
}

export class ErrorBoundary extends Component<Props, State> {
  private errorTracking = ErrorTrackingService.getInstance()

  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, errorId: 0 }
    this.handleWindowError = this.handleWindowError.bind(this)
    this.resetError = this.resetError.bind(this)
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, componentName, metadata } = this.props

    // Track error
    this.errorTracking.captureError(error, {
      componentName,
      ...metadata,
      errorInfo: errorInfo.componentStack,
    })

    // Call custom error handler
    onError?.(error)
  }

  componentDidMount() {
    window.addEventListener('error', this.handleWindowError)
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleWindowError)
  }

  handleWindowError = (event: ErrorEvent) => {
    event.preventDefault()
    const error = event.error || new Error(event.message)
    this.setState({ hasError: true, error })

    // Track window error
    this.errorTracking.captureError(error, {
      componentName: this.props.componentName,
      ...this.props.metadata,
      source: 'window',
    })

    this.props.onError?.(error)
  }

  resetError = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: undefined,
      errorId: (prevState.errorId || 0) + 1,
    }))
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div role="alert" className="error-boundary">
            <h2>Something went wrong</h2>
            <details>
              <summary>Error details</summary>
              <pre>{this.state.error?.message}</pre>
            </details>
            <button
              onClick={this.resetError}
              className="mt-4 px-4 py-2 bg-primary text-white rounded"
            >
              Try again
            </button>
          </div>
        )
      )
    }

    // Use errorId as key to force re-mount after error reset
    return <div key={this.state.errorId}>{this.props.children}</div>
  }
}
